/**
 * useWalletBalance Hook
 * Fetch wallet balance using the walletDataAggregator (DeBank → Moralis → Alchemy)
 * Uses React Query for caching and automatic refetching
 */

import { useQuery } from '@tanstack/react-query';
import { getTotalValue, getTokenBalances, getProviderStatus, getBestProvider } from '../../services/walletDataAggregator';

export interface WalletBalanceData {
  address: string;
  ethBalance: string;
  ethBalanceFormatted: string;
  ethBalanceUSD?: number;
  tokenBalances: Array<{
    symbol: string;
    name: string;
    balance: number;
    balanceUSD: number;
    price: number;
    logoUrl?: string;
  }>;
  tokenCount: number;
  totalValueUSD?: number;
  provider: string | null;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Hook to fetch wallet balance
 * Uses walletDataAggregator which tries DeBank → Moralis → Alchemy
 */
export function useWalletBalance(
  address: string | null,
  options: {
    enabled?: boolean;
  } = {}
) {
  const { enabled = true } = options;

  // Check if any provider is available
  const providerStatus = getProviderStatus();
  const hasProvider = providerStatus.debank.available || providerStatus.moralis.available || providerStatus.alchemy.available;

  return useQuery({
    queryKey: ['wallet-balance', address],
    queryFn: async (): Promise<WalletBalanceData> => {
      if (!address) {
        throw new Error('No address provided');
      }

      // Check provider availability
      if (!hasProvider) {
        console.warn('⚠️ No wallet data provider configured');
        return {
          address,
          ethBalance: '0',
          ethBalanceFormatted: '0',
          tokenBalances: [],
          tokenCount: 0,
          provider: null,
          isLoading: false,
          error: null,
        };
      }

      try {
        // Fetch total value and token balances
        const [totalValue, tokens] = await Promise.all([
          getTotalValue(address),
          getTokenBalances(address),
        ]);

        // Find ETH balance from tokens
        const ethToken = tokens.find(t =>
          t.chain === 'ethereum' &&
          (t.symbol === 'ETH' || t.address === '0x0000000000000000000000000000000000000000')
        );

        const ethBalance = ethToken?.balance || 0;
        const ethBalanceUSD = ethToken?.balanceUSD || 0;

        return {
          address,
          ethBalance: ethBalance.toString(),
          ethBalanceFormatted: ethBalance.toFixed(4),
          ethBalanceUSD,
          tokenBalances: tokens.map(t => ({
            symbol: t.symbol,
            name: t.name,
            balance: t.balance,
            balanceUSD: t.balanceUSD,
            price: t.price,
            logoUrl: t.logoUrl,
          })),
          tokenCount: tokens.length,
          totalValueUSD: totalValue,
          provider: getBestProvider(),
          isLoading: false,
          error: null,
        };
      } catch (error) {
        console.error('❌ Error fetching wallet balance:', error);
        throw error;
      }
    },
    enabled: !!address && enabled && hasProvider,
    staleTime: 30000, // 30 seconds
    gcTime: 300000, // 5 minutes cache
    refetchInterval: 60000, // Refetch every 60 seconds
    retry: 2,
    retryDelay: 1000,
  });
}

/**
 * Format large numbers with K, M, B suffixes
 */
export function formatNumber(num: number, decimals: number = 2): string {
  if (num === 0) return '0';

  const absNum = Math.abs(num);

  if (absNum >= 1e9) {
    return (num / 1e9).toFixed(decimals) + 'B';
  } else if (absNum >= 1e6) {
    return (num / 1e6).toFixed(decimals) + 'M';
  } else if (absNum >= 1e3) {
    return (num / 1e3).toFixed(decimals) + 'K';
  } else {
    return num.toFixed(decimals);
  }
}

/**
 * Format currency (USD)
 */
export function formatCurrency(amount: number, decimals: number = 2): string {
  if (amount === 0) return '$0';

  const formatted = formatNumber(amount, decimals);
  return '$' + formatted;
}
